
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Mobile Safari detection utility
const isMobileSafari = () => {
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const webkit = /WebKit/.test(ua);
  const standalone = (window.navigator as any).standalone;
  return iOS && webkit && !standalone;
};

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [delayedRedirect, setDelayedRedirect] = useState(false);
  const isMobile = isMobileSafari();

  // Mobile Safari specific delay buffer
  const redirectDelay = isMobile ? 2000 : 500; // 2 seconds for mobile Safari, 500ms for others

  console.log('=== PROTECTED ROUTE AUTH DEBUG ===');
  console.log('Is Mobile Safari:', isMobile);
  console.log('Auth loading:', loading);
  console.log('User authenticated:', !!user);
  console.log('User email:', user?.email);
  console.log('Current path:', location.pathname);
  console.log('Redirect delay:', redirectDelay);
  console.log('==================================');

  useEffect(() => {
    if (!loading && !user) {
      console.log('User not authenticated, setting up redirect timer');
      
      // Add a delay before redirecting, especially important for mobile Safari
      const timer = setTimeout(() => {
        console.log('Redirect timer expired, redirecting to /auth');
        setDelayedRedirect(true);
        navigate("/auth", { replace: true });
      }, redirectDelay);

      return () => {
        console.log('Clearing redirect timer');
        clearTimeout(timer);
      };
    } else if (user) {
      console.log('User authenticated, clearing any delayed redirect');
      setDelayedRedirect(false);
    }
  }, [user, loading, navigate, redirectDelay]);

  if (loading) {
    console.log('Auth still loading, showing loading state');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600">
            {isMobile ? "Loading authentication... Please wait." : "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  // Show extended loading state for mobile Safari before redirect
  if (!user && !delayedRedirect && isMobile) {
    console.log('Mobile Safari: Showing extended loading before potential redirect');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600">Authenticating... Please wait a moment.</p>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log('No user and not on mobile Safari, will redirect immediately');
    return null;
  }

  console.log('User authenticated, rendering protected content');
  return <>{children}</>;
};

export default ProtectedRoute;
